import { Button } from '@ui/button.tsx';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { Link } from '@tanstack/react-router';
import pointsTranslation from '@r/translations/points-translation.ts';
import { ChevronRightIcon, FolderInputIcon, FactoryIcon, TrophyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { ListSkeleton } from '@r/components/kpi/skeleton/list-skeleton.tsx';
import { useMemo } from 'react';
import {
  useApiAdmininsightsperformancesGetCollection,
  useApiAdmininsightspointsGetCollection
} from '@r/api/generated/client.ts';
import type {
  CompanyInsightDashboardReadModel,
  CompanyInsightPerformanceReadModel,
  CompanyInsightPointReadModel,
  ApiAdmininsightsperformancesGetCollectionParams,
  ApiAdmininsightspointsGetCollectionParams,
  ApiAdmininsightsperformancesGetCollectionStatusItem,
  ApiAdmininsightspointsGetCollectionStatusItem
} from '@r/api/generated/client.schemas.ts';

import type { Option } from '@r/components/kpi/filters/select-tag-input.tsx';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { cn } from '@r/utils/utils.ts';

type PerformanceRankingCompany = CompanyInsightPerformanceReadModel;
type PointRankingCompany = CompanyInsightPointReadModel;
type DashboardRankingCompany = CompanyInsightDashboardReadModel;
type RankingCompany = PerformanceRankingCompany | PointRankingCompany | DashboardRankingCompany;

const podiumBadgeStyles: Record<number, string> = {
  1: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  2: 'bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
  3: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300'
};

const RankBadge = ({ rank }: { rank: number }) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-center rounded-full size-8 text-sm font-bold tabular-nums',
      podiumBadgeStyles[rank] ?? 'bg-muted text-muted-foreground'
    )}
  >
    {rank === 1 ? <TrophyIcon size={16} /> : rank}
  </div>
);

interface CompanyListProps {
  className?: string;
  rankingValueType?: 'monetary' | 'point' | null;
  orderBy?: string | null;
  direction?: 'asc' | 'desc';
  hideImportBtn?: boolean;
  filters?: {
    onlyCompaniesRegistered: boolean;
    companyTypologies: Option[];
    sales?: string[];
    dateRange?: { from?: string; to?: string };
    status?: string[];
  };
  rankingItems?: RankingCompany[];
  rankingLoading?: boolean;
  rankingError?: boolean;
  disableInternalFetch?: boolean;
}

export const CompanyRankingList = ({
  className,
  rankingValueType = null,
  hideImportBtn = false,
  filters,
  rankingItems,
  rankingLoading,
  rankingError,
  disableInternalFetch
}: CompanyListProps) => {
  const { t } = useTranslation();

  const { onlyCompaniesRegistered } = filters || {};
  const { companyTypologies, sales, dateRange, status } =
    filters ||
    ({ companyTypologies: [] } as {
      companyTypologies: Option[];
      sales?: string[];
      dateRange?: { from?: string; to?: string };
      status?: string[];
    });

  const statusesToSend = useMemo(() => {
    return status && status.length > 0
      ? (status as ApiAdmininsightsperformancesGetCollectionStatusItem[])
      : typeof onlyCompaniesRegistered === 'boolean' && onlyCompaniesRegistered
        ? (['company.status.registered'] as ApiAdmininsightsperformancesGetCollectionStatusItem[])
        : undefined;
  }, [status, onlyCompaniesRegistered]);

  const performancesParams: ApiAdmininsightsperformancesGetCollectionParams | undefined =
    useMemo(() => {
      if (rankingValueType !== 'monetary') return undefined;
      return {
        ...(statusesToSend ? { 'status[]': statusesToSend } : {}),
        ...(companyTypologies && companyTypologies.length > 0
          ? { 'typology[]': companyTypologies.map((t: Option) => String(t.id)) }
          : {}),
        ...(sales && sales.length > 0 ? { 'sales[]': sales } : {}),
        ...(dateRange?.from ? { from: dateRange.from } : {}),
        ...(dateRange?.to ? { to: dateRange.to } : {})
      };
    }, [rankingValueType, statusesToSend, companyTypologies, sales, dateRange]);

  const pointsParams: ApiAdmininsightspointsGetCollectionParams | undefined = useMemo(() => {
    if (rankingValueType !== 'point') return undefined;
    return {
      ...(statusesToSend
        ? { 'status[]': statusesToSend as ApiAdmininsightspointsGetCollectionStatusItem[] }
        : {}),
      ...(companyTypologies && companyTypologies.length > 0
        ? { 'typology[]': companyTypologies.map((t: Option) => String(t.id)) }
        : {}),
      ...(sales && sales.length > 0 ? { 'sales[]': sales } : {}),
      ...(dateRange?.from ? { from: dateRange.from } : {}),
      ...(dateRange?.to ? { to: dateRange.to } : {})
    };
  }, [rankingValueType, statusesToSend, companyTypologies, sales, dateRange]);

  const hasExternalRanking = Boolean(
    disableInternalFetch || rankingItems || rankingLoading || rankingError
  );

  const performancesQuery = useApiAdmininsightsperformancesGetCollection(performancesParams, {
    query: { enabled: rankingValueType === 'monetary' && !hasExternalRanking }
  });

  const pointsQuery = useApiAdmininsightspointsGetCollection(pointsParams, {
    query: { enabled: rankingValueType === 'point' && !hasExternalRanking }
  });
  useOrvalQueryErrorToast(performancesQuery, t('toast.error.label'));
  useOrvalQueryErrorToast(pointsQuery, t('toast.error.label'));

  const companiesRankingQuery = useMemo(() => {
    if (rankingError) {
      return {
        isLoading: false,
        isSuccess: false,
        isError: true,
        data: undefined
      };
    }
    if (disableInternalFetch && !rankingItems) {
      return {
        isLoading: Boolean(rankingLoading),
        isSuccess: Boolean(rankingItems),
        isError: false,
        data: rankingItems
      };
    }
    if (rankingLoading) {
      return {
        isLoading: true,
        isSuccess: false,
        isError: false,
        data: undefined
      };
    }
    if (rankingItems) {
      return {
        isLoading: false,
        isSuccess: true,
        isError: false,
        data: rankingItems
      };
    }
    if (rankingValueType === 'monetary') {
      return {
        ...performancesQuery,
        data: performancesQuery.data?.insightCompany.ranking as
          | PerformanceRankingCompany[]
          | undefined
      };
    }
    if (rankingValueType === 'point') {
      return {
        ...pointsQuery,
        data: pointsQuery.data?.insightCompany.ranking as PointRankingCompany[] | undefined
      };
    }
    return {
      isLoading: false,
      isSuccess: false,
      isError: false,
      data: undefined
    };
  }, [
    disableInternalFetch,
    rankingError,
    rankingLoading,
    rankingItems,
    rankingValueType,
    performancesQuery,
    pointsQuery
  ]);

  const rankedCompanies = (companiesRankingQuery.data ?? []).map((company: RankingCompany) => {
    let value: number | null = null;
    let label: string | null = null;

    if (rankingValueType === 'monetary') {
      value = (company as PerformanceRankingCompany).caAmount;
      label = `${value.toLocaleString()} €`;
    }
    if (rankingValueType === 'point') {
      value = (company as PointRankingCompany).acquiredPoints;
      label = pointsTranslation(t, value, true);
    }

    return { company, value, label };
  });
  const maxValue = Math.max(0, ...rankedCompanies.map(({ value }) => value ?? 0));

  return (
    <>
      {(companiesRankingQuery.isLoading || companiesRankingQuery.isError) && <ListSkeleton />}

      {companiesRankingQuery.isSuccess && (
        <Card className={`flex sticky top-5 flex-col w-full ${className}`}>
          <CardHeader className="space-y-0 shrink-0">
            <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
              <FactoryIcon />

              <div className="text-xl font-bold">{t('admin.dashboard.companies.list.title')}</div>
            </CardTitle>
          </CardHeader>

          <CardContent className="flex flex-col flex-1 font-regular">
            {rankedCompanies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 space-y-2 text-center grow text-muted-foreground">
                <FactoryIcon size={28} />
                <p className="text-sm">{t('admin.dashboard.companies.list.empty')}</p>
              </div>
            ) : (
              <ol className="space-y-0.5">
                {rankedCompanies.map(({ company, value, label }, index) => (
                  <li key={company.id}>
                    <Link
                      to="/companies/$id"
                      params={{ id: String(company.id) }}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl transition-colors group hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <RankBadge rank={index + 1} />

                      <div className="flex flex-col flex-1 min-w-0 gap-1.5">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="font-medium truncate" title={company.name}>
                            {company.name}
                          </span>
                          {label && (
                            <span className="text-sm font-semibold shrink-0 tabular-nums">
                              {label}
                            </span>
                          )}
                        </div>

                        {value !== null && maxValue > 0 && (
                          <div className="w-full h-[2px] overflow-hidden rounded-full bg-muted group-hover:bg-background">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${Math.max((value / maxValue) * 100, 2)}%` }}
                            />
                          </div>
                        )}
                      </div>

                      <ChevronRightIcon
                        size={16}
                        className="transition-opacity opacity-0 shrink-0 text-muted-foreground group-hover:opacity-100 group-focus-visible:opacity-100"
                      />
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>

          <CardFooter className="shrink-0">
            <div className="flex flex-wrap 2xl:flex-nowrap 2xl:space-x-2.5 space-y-2.5 2xl:space-y-0 w-full">
              <LinkProtected
                to="/companies"
                className={`w-full ${!hideImportBtn ? '2xl:w-6/12' : ''}`}
              >
                <Button rounded="full" className="w-full">
                  <FactoryIcon className="mr-2.5" />
                  {t('admin.dashboard.companies.btn')}
                </Button>
              </LinkProtected>

              {!hideImportBtn && (
                <LinkProtected to="/companies/import" className="w-full 2xl:w-6/12">
                  <Button rounded="full" className="w-full" variant={'outline'}>
                    <FolderInputIcon className="mr-2.5" />
                    {t('admin.dashboard.companies.import.btn')}
                  </Button>
                </LinkProtected>
              )}
            </div>
          </CardFooter>
        </Card>
      )}
    </>
  );
};
